import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle2,
  Upload,
  FileText,
  ShieldCheck,
  UserPlus,
  Globe,
  Award,
  Mail,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../lib/api";
import { PHONE_CODES, YEARS_EXPERIENCE, COUNTRIES } from "../lib/data";
import { toast } from "sonner";

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[13px] font-medium text-slate-700">
      {label} {required && <span className="text-[#C49A3A]">*</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={
      "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm input-focus " +
      (props.className || "")
    }
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={
      "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm input-focus " +
      (props.className || "")
    }
  >
    {children}
  </select>
);

function CompactFileUpload({ label, hint, file, onFile }) {
  return (
    <div
      className={`rounded-lg border border-dashed ${
        file ? "border-emerald-300 bg-emerald-50/40" : "border-slate-300 bg-slate-50/60"
      } p-3.5 transition-colors`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
            {label}
            <span className="text-[10px] font-medium text-[#C49A3A] uppercase tracking-wider">
              Required
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 truncate">{hint}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            PDF, JPG, PNG · up to 10 MB
          </div>
        </div>
        <label className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-slate-300 px-3 py-1.5 rounded-md text-[12px] font-medium text-slate-700 hover:border-cct-blue hover:text-cct-blue cursor-pointer transition-colors">
          <Upload className="w-3.5 h-3.5" /> Choose
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => onFile(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      {file && (
        <div className="mt-2 flex items-center gap-2 text-[12px] bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
          <FileText className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span className="text-slate-700 truncate flex-1">{file.name}</span>
          <button
            onClick={() => onFile(null)}
            className="text-[11px] text-slate-500 hover:text-red-500"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

const FEATURES = [
  {
    icon: Globe,
    title: "Global Partner Network",
    desc: "Join recruiters referring students worldwide to Cambridge College programs.",
  },
  {
    icon: Award,
    title: "Competitive Commissions",
    desc: "Earn on every successfully enrolled student you refer to CCT.",
  },
  {
    icon: ShieldCheck,
    title: "Verified & Trusted",
    desc: "All documents transmitted securely and reviewed by CCT admissions staff.",
  },
];

export default function RecruiterSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    email: "",
    phone: "",
    phone_code: "+1",
    country: "",
    city: "",
    years_experience: "",
    message: "",
  });
  const [idDoc, setIdDoc] = useState(null);
  const [licDoc, setLicDoc] = useState(null);
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async () => {
    const required = [
      "full_name",
      "company_name",
      "email",
      "phone",
      "country",
      "city",
      "years_experience",
    ];
    for (const k of required)
      if (!form[k]) {
        toast.error(`Please fill: ${k.replace(/_/g, " ")}`);
        return;
      }
    if (!idDoc || !licDoc) {
      toast.error("Please upload both required documents");
      return;
    }
    if (!agree1 || !agree2) {
      toast.error("Please accept the acknowledgements");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      fd.append("id_document", idDoc);
      fd.append("business_license", licDoc);
      const { data } = await api.post("/recruiters/signup", fd);
      setSubmitted(data);
      toast.success("Application submitted. Await approval.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cct-ambient">
        <Header minimal />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center fade-up">
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 12 }}
            className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 items-center justify-center shadow-lg shadow-emerald-200"
          >
            <CheckCircle2 className="w-11 h-11 text-white" />
          </motion.div>
          <h1 className="mt-6 font-display text-4xl font-semibold text-cct-blue">
            Application Submitted
          </h1>
          <p className="mt-3 text-slate-600 max-w-md mx-auto">
            Thanks for applying to our partner network. Our admissions team
            will review your documents and send you a recruiter agreement
            within 1–3 business days.
          </p>
          <div className="mt-6 inline-block bg-white border border-slate-200 rounded-lg px-5 py-3 text-sm">
            Current status:{" "}
            <span className="font-semibold text-amber-600">Pending review</span>
          </div>
          <div className="mt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-cct-blue hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> Back to portal
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cct-ambient">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cct-blue mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back to portal
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
          {/* Left: Intro / marketing */}
          <motion.aside
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:sticky lg:top-6 self-start"
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#2A4FAE] text-white p-6 sm:p-7 shadow-xl">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#C49A3A]/30 blur-3xl" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold text-amber-300 bg-white/10 px-2.5 py-1 rounded-full">
                  <UserPlus className="w-3 h-3" /> Recruiter Application
                </div>
                <h1 className="mt-4 font-display text-3xl sm:text-[34px] leading-[1.1] font-semibold">
                  Become a <span className="text-amber-300 italic">Cambridge</span>{" "}
                  Partner
                </h1>
                <p className="mt-3 text-white/75 text-sm leading-relaxed">
                  Complete the short application and upload your identification
                  and business license. Our team reviews every partner
                  personally and sends your recruiter agreement once approved.
                </p>
              </div>
              <div className="relative mt-6 space-y-4">
                {FEATURES.map((f, i) => (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.08 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/15 border border-white/10 flex items-center justify-center shrink-0">
                      <f.icon className="w-4 h-4 text-amber-300" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{f.title}</div>
                      <div className="text-[12px] text-white/70 leading-relaxed">
                        {f.desc}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="relative mt-6 pt-5 border-t border-white/10 text-[12px] text-white/70">
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> info@cambridgecollegebc.com
                </div>
              </div>
            </div>
            <div className="mt-4 text-[12px] text-slate-500 text-center lg:text-left">
              Already a partner?{" "}
              <Link
                to="/recruiter/login"
                className="text-cct-blue font-medium hover:underline"
              >
                Sign in →
              </Link>
            </div>
          </motion.aside>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-7 space-y-6"
          >
            <div>
              <h2 className="font-display text-xl font-semibold text-cct-blue">
                Your details
              </h2>
              <p className="text-[13px] text-slate-500">
                All fields marked with * are required.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
              <Field label="Full Name" required className="sm:col-span-3">
                <Input
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                />
              </Field>
              <Field label="Company Name" required className="sm:col-span-3">
                <Input
                  value={form.company_name}
                  onChange={(e) => update("company_name", e.target.value)}
                />
              </Field>
              <Field label="Email Address" required className="sm:col-span-3">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                />
              </Field>
              <Field label="Phone Number" required className="sm:col-span-3">
                <div className="flex gap-2">
                  <Select
                    value={form.phone_code}
                    onChange={(e) => update("phone_code", e.target.value)}
                    className="w-24"
                  >
                    {PHONE_CODES.map((p) => (
                      <option key={p.region} value={p.code}>
                        {p.region} {p.code}
                      </option>
                    ))}
                  </Select>
                  <Input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                  />
                </div>
              </Field>
              <Field label="Country" required className="sm:col-span-2">
                <Select
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                >
                  <option value="">— Select country —</option>
                  {COUNTRIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </Select>
              </Field>
              <Field label="City" required className="sm:col-span-2">
                <Input
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                />
              </Field>
              <Field
                label="Years of Experience"
                required
                className="sm:col-span-2"
              >
                <Select
                  value={form.years_experience}
                  onChange={(e) => update("years_experience", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {YEARS_EXPERIENCE.map((y) => (
                    <option key={y}>{y}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Message / Notes" className="sm:col-span-6">
                <textarea
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={2}
                  placeholder="Tell us briefly about your recruitment focus (optional)"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm input-focus"
                />
              </Field>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <h3 className="font-display text-base font-semibold text-cct-blue">
                Required Documents
              </h3>
              <p className="text-[12px] text-slate-500 mb-3">
                Files are transmitted securely and visible only to CCT staff.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CompactFileUpload
                  label="Government-issued ID"
                  hint="Passport, driving license, or national ID"
                  file={idDoc}
                  onFile={setIdDoc}
                />
                <CompactFileUpload
                  label="Business License"
                  hint="Incorporation or trade license"
                  file={licDoc}
                  onFile={setLicDoc}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 space-y-2">
              <label className="flex items-start gap-2.5 text-[13px] text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree1}
                  onChange={(e) => setAgree1(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#1E3A8A]"
                />
                <span>
                  I agree to the initial terms and consent to Cambridge College
                  reviewing my application. I understand the recruiter
                  agreement will be sent for signature after initial approval.
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-[13px] text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree2}
                  onChange={(e) => setAgree2(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#1E3A8A]"
                />
                <span>
                  I confirm the information and documents provided above are
                  accurate and authentic.
                </span>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
              <div className="text-[12px] text-slate-500">
                <ShieldCheck className="inline w-3.5 h-3.5 mr-1 text-cct-blue" />
                Secure submission · SSL encrypted
              </div>
              <button
                onClick={onSubmit}
                disabled={submitting}
                className="w-full sm:w-auto bg-cct-blue text-white text-sm font-semibold px-6 py-2.5 rounded-md hover:bg-[#1a3380] disabled:opacity-60 shadow-sm"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
